import {
  Button,
  Form,
  InputNumber,
  Modal,
  Radio,
  Select,
  Space,
  Spin,
} from 'antd';
import { EditOutlined } from '@ant-design/icons';
import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { dashboardRoute } from '../../../constants/pathname';
import { GoodData, GoodsSkuProductData } from '../../../types';
import { alertMessage } from '../../../utils/alertMessage';
import {
  getDataWithAuthToken,
  postDataWithAuthToken,
} from '../../../utils/axiosRequest';
import getDashboardStyle from '../../../utils/getDashboardStyle';
import GoodStockModal from '../goodDetail/GoodStockModal';

type OrderSelectAttrModalProps = {
  visible: boolean;
  setVisible: React.Dispatch<React.SetStateAction<boolean>>;
  selectedGoods?: any;
  setSelectedGoods?: React.Dispatch<React.SetStateAction<any>>;
  callBack?: () => void;
  userId: number;
  currentGood?: GoodData;
  currentGoodIndex?: number;
  orderId?: number;
  version?: number; // 乐观锁版本号：编辑已有订单时随预览带回，早于确认发现并发冲突
};
const SelectGoodAttrModal = ({
  visible,
  setVisible,
  selectedGoods,
  callBack,
  setSelectedGoods,
  userId,
  currentGood,
  currentGoodIndex,
  orderId,
  version,
}: OrderSelectAttrModalProps) => {
  const { t } = useTranslation();
  const [goodDetail, setGoodDetail] = useState<GoodData>();
  const [form] = Form.useForm();
  const [isLoading, setIsLoading] = useState<boolean>(false);
  const [customPrice, setCustomPrice] = useState<number>();
  const [customRate, setCustomRate] = useState<number>();
  // 折扣/加价的基准价类型:0=正常价 1=促销价 2=会员价
  const [discountBaseType, setDiscountBaseType] = useState<number>(0);
  const [showStockModal, setShowStockModal] = useState<boolean>(false);
  const [matchedProduct, setMatchedProduct] = useState<GoodsSkuProductData>();

  // 实际渲染出 Select 的规格维度(有 sku 值的),字段名沿用 specAttrName
  const specAttrNames = useMemo(
    () =>
      (goodDetail?.sku?.specTypeAttr || [])
        .filter((attr) => attr.goodsSkuAttr && attr.goodsSkuAttr.length)
        .map((attr) => attr.specAttrName),
    [goodDetail]
  );

  // 选满所有维度后,把选中的 goodsSkuAttrId 拼成 p{id} 集合,反查唯一 product
  const resolveProduct = useCallback(() => {
    const products = goodDetail?.sku?.goodsSkuProduct;
    if (!products) {
      setMatchedProduct(undefined);
      return;
    }
    const selectedKeys = specAttrNames
      .map((name) => form.getFieldValue(name))
      .filter((v) => v !== undefined && v !== null)
      .map((id) => `p${id}`);

    // 未选满则无法唯一确定
    if (selectedKeys.length !== specAttrNames.length) {
      setMatchedProduct(undefined);
      return;
    }

    // key 集合双向完全相等才算命中(避免单维度误命中多 key 的 product)
    const found = products.find((p) => {
      const keys = Object.keys(p.goodsSkuAttrIds || {});
      return (
        keys.length === selectedKeys.length &&
        selectedKeys.every((k) => keys.includes(k))
      );
    });
    setMatchedProduct(found);
  }, [goodDetail, specAttrNames, form]);

  // 选中 sku 的规格加价(缺失一律按 0,避免 Number(undefined)=NaN 传成 null)
  const specPrice = useMemo(
    () => (matchedProduct ? Number(matchedProduct.productPrice) || 0 : 0),
    [matchedProduct]
  );

  const getGoodDetail = useCallback(() => {
    setIsLoading(true);
    getDataWithAuthToken('goods/detail', {
      params: {
        goodsId: currentGood && currentGood.goodsId,
      },
    })
      .then((response) => {
        if (response && response.goodStatus) {
          setGoodDetail(response.data);
        } else {
          alertMessage(
            'error',
            response?.msg || t('general.noResponse'),
            response?.data || undefined
          );
        }
        setIsLoading(false);
      })
      .catch((err) => {
        setIsLoading(false);
        console.log(err);
      });
  }, [t, currentGood]);

  useEffect(() => {
    if (visible) {
      form.resetFields();
      setCustomPrice(undefined);
      setCustomRate(undefined);
      setDiscountBaseType(0);
      setMatchedProduct(undefined);
      getGoodDetail();
    }
  }, [visible, getGoodDetail, form]);

  const onConfirm = () => {
    form
      .validateFields()
      .then((values) => {
        const { goodsNumber, shopPrice, goodsWeight, ...rest } = values;
        postDataWithAuthToken('order/preview_goods', {
          userId: userId,
          sellerId:
            goodDetail && goodDetail.seller ? goodDetail.seller.sellerId : 0,
          orderId: orderId,
          version: version, // 乐观锁前置提醒：预览时即比对版本
          orderGoodsList: [
            ...(selectedGoods || []).map((good: any, index: number) => ({
              goodsId: good.goodsId,
              goodsName: good.goodsName,
              goodsWeight: good.goodsWeight,
              goodsNumber:
                currentGoodIndex !== undefined && index === currentGoodIndex
                  ? goodsNumber
                  : good.goodsNumber,
              goodsSkuAttrIds:
                (good.goodsAttrId && good.goodsAttrId.split(',')) || [],
              recId: good.recId || undefined,
              goodsPrice: good.goodsPrice,
              mainGoodsPrice: good.mainGoodsPrice,
            })),
            ...(currentGoodIndex !== undefined
              ? []
              : [
                  {
                    goodsId: (goodDetail && goodDetail.goodsId) || undefined,
                    goodsName:
                      (goodDetail && goodDetail.goodsName) || undefined,
                    goodsNumber: goodsNumber,
                    goodsSkuAttrIds: Object.values(rest),
                    // 有规格时,各基础价 + 选中 sku 的 productPrice;自定义价原样发
                    goodsPrice: values.shopPrice
                      ? values.shopPrice === 1
                        ? matchedProduct && goodDetail
                          ? (Number(goodDetail.promotePrice) || 0) + specPrice
                          : goodDetail && goodDetail.promotePrice
                        : values.shopPrice === 2
                        ? matchedProduct && goodDetail
                          ? (Number(goodDetail.memberPrice) || 0) + specPrice
                          : goodDetail && goodDetail.memberPrice
                        : customPrice
                      : matchedProduct && goodDetail
                      ? (Number(goodDetail.shopPrice) || 0) + specPrice
                      : undefined,
                    mainGoodsPrice:
                      goodDetail && goodDetail.mainGoods
                        ? goodDetail.mainGoods.shopPrice
                        : undefined,
                    goodsWeight: goodsWeight,
                  },
                ]),
          ],
        })
          .then((response) => {
            if (response && response.goodStatus) {
              setIsLoading(false);
              setSelectedGoods &&
                setSelectedGoods((prev: any) => ({
                  ...prev,
                  orderGoodsList: response.data.orderGoodsList,
                  orderGoodsListTotal: response.data.goodsAmount,
                  shippingTransportId: response.data.transportId,
                }));
              onClose();
              callBack && callBack();
            } else {
              setIsLoading(false);
              alertMessage(
                'error',
                response?.msg || t('general.noResponse'),
                response?.data || undefined
              );
            }
          })
          .catch((err) => {
            setIsLoading(false);
            console.log(err);
          });
      })
      .catch((err) => {
        setIsLoading(false);
        console.log(err);
      });
  };

  const onClose = () => {
    setGoodDetail(undefined);
    setVisible(false);
  };

  // 根据所选基准类型返回折扣/加价的基准价(含规格加价 specPrice)
  const getDiscountBasePrice = () => {
    if (!goodDetail) return 0;
    if (discountBaseType === 1) {
      return (Number(goodDetail.promotePrice) || 0) + specPrice;
    }
    if (discountBaseType === 2) {
      return (Number(goodDetail.memberPrice) || 0) + specPrice;
    }
    return (Number(goodDetail.shopPrice) || 0) + specPrice;
  };

  return (
    <>
    <Modal
      title={t('order.add/editOrder.selectAttr')}
      visible={visible}
      onCancel={onClose}
      onOk={onConfirm}
      okText={t('order.add/editOrder.ok')}
      cancelText={t('order.add/editOrder.cancel')}
    >
      <Spin spinning={isLoading}>
        <Form
          form={form}
          onValuesChange={resolveProduct}
          initialValues={{
            goodsNumber:
              currentGoodIndex !== undefined && currentGood
                ? currentGood.goodsNumber
                : 1,
            goodsWeight: currentGood ? currentGood.goodsWeight : '',
            shopPrice: 0,
          }}
        >
          {goodDetail && (
            <>
              <Form.Item label={t('goods.goodsListColumns.goodsName')}>
                <Button
                  type="link"
                  style={{ padding: 0, height: 'auto', textAlign: 'left' }}
                  onClick={() =>
                    window.open(
                      `${dashboardRoute.goods.detail}?good_id=${goodDetail.goodsId}`,
                      '_blank'
                    )
                  }
                >
                  {goodDetail.goodsName}
                </Button>
              </Form.Item>
              <Form.Item label={t('goods.goodsListColumns.goodsUnit')}>
                {goodDetail.goodsUnit}
              </Form.Item>
            </>
          )}
          {currentGoodIndex === undefined && goodDetail && (
            <>
              {goodDetail.sku &&
                goodDetail.sku.specTypeAttr &&
                goodDetail.sku.specTypeAttr.map(
                  (attr) =>
                    attr.goodsSkuAttr &&
                    !!attr.goodsSkuAttr.length && (
                      <Form.Item
                        name={attr.specAttrName}
                        label={attr.specAttrName}
                        rules={[
                          {
                            required: true,
                            message: t('general.inputError.pleaseSelectOne'),
                          },
                        ]}
                        key={attr.specAttrId}
                      >
                        <Select style={{ width: '100%' }}>
                          {attr.goodsSkuAttr.map((a) => (
                            <Select.Option
                              value={a.goodsSkuAttrId}
                              key={a.goodsSkuAttrId}
                            >
                              {a.skuAttrValue}
                            </Select.Option>
                          ))}
                        </Select>
                      </Form.Item>
                    )
                )}
              <Form.Item
                name="shopPrice"
                label={t('goods.add/editGood.shopPrice')}
                style={{marginBottom: 0}}
                rules={[
                  {
                    required: true,
                    message: t('general.inputError.pleaseSelectOne'),
                  },
                  () => ({
                    validator(rule, value) {
                      if (
                        value === 3 &&
                        (customPrice === null || customPrice === undefined)
                      ) {
                        return Promise.reject(
                          new Error(t('general.inputError.empty'))
                        );
                      }
                      return Promise.resolve();
                    },
                  }),
                ]}
              >
                <Radio.Group>
                  <Radio value={0}>
                    {`${t('goods.add/editGood.normalPrice')}${
                      goodDetail.sku
                        ? matchedProduct
                          ? ` ${(
                              (Number(goodDetail.shopPrice) || 0) + specPrice
                            ).toFixed(2)}`
                          : ''
                        : ` ${goodDetail.shopPrice}`
                    }`}
                  </Radio>
                  {goodDetail.promotePrice && (
                    <Radio value={1}>
                      {`${t('goods.add/editGood.promotePrice')} ${
                        matchedProduct
                          ? (
                              (Number(goodDetail.promotePrice) || 0) + specPrice
                            ).toFixed(2)
                          : goodDetail.promotePrice
                      }`}
                    </Radio>
                  )}
                  {goodDetail.memberPrice && goodDetail.memberPrice > 0 && (
                    <Radio value={2}>
                      {`${t('goods.goodsListColumns.memberPrice')} ${
                        matchedProduct
                          ? (
                              (Number(goodDetail.memberPrice) || 0) + specPrice
                            ).toFixed(2)
                          : goodDetail.memberPrice
                      }`}
                    </Radio>
                  )}
                  {!getDashboardStyle().isSalesForceAppType && (
                    <Radio value={3}>
                      <InputNumber
                        type="number"
                        min={0}
                        placeholder={t('goods.add/editGood.custom')}
                        value={customPrice}
                        onChange={(value) => {
                          setCustomPrice(value !== null ? value : undefined);
                        }}
                      />
                    </Radio>
                  )}
                </Radio.Group>
              </Form.Item>
              {!getDashboardStyle().isSalesForceAppType && (
                <Space
                  direction="vertical"
                  size={8}
                  style={{ marginTop: 8 }}
                >
                    <Space size={8} wrap>
                      {/* 折扣/加价基准价选择 */}
                      <Select
                        value={discountBaseType}
                        onChange={(value) => setDiscountBaseType(value)}
                        style={{ width: 110 }}
                      >
                        <Select.Option value={0}>
                          {t('goods.add/editGood.normalPrice')}
                        </Select.Option>
                        {!!goodDetail.promotePrice && (
                          <Select.Option value={1}>
                            {t('goods.add/editGood.promotePrice')}
                          </Select.Option>
                        )}
                        {!!goodDetail.memberPrice &&
                          goodDetail.memberPrice > 0 && (
                            <Select.Option value={2}>
                              {t('goods.goodsListColumns.memberPrice')}
                            </Select.Option>
                          )}
                      </Select>
                      <InputNumber
                        type="number"
                        placeholder={t('goods.add/editGood.custom')}
                        value={customRate}
                        onChange={(value) => {
                          setCustomRate(value !== null ? value : undefined);
                        }}
                        addonAfter="%"
                        style={{ width: 120 }}
                      />
                      <Button
                        type="primary"
                        ghost
                        disabled={
                          customRate === null || customRate === undefined
                        }
                        onClick={() => {
                          const basePrice = getDiscountBasePrice();
                          if (!basePrice || customRate === undefined) return;
                          setCustomPrice(
                            Number(
                              (basePrice * (1 + customRate / 100)).toFixed(2)
                            )
                          );
                          form.setFieldsValue({ shopPrice: 3 });
                        }}
                      >
                        {t('general.ok')}
                      </Button>
                    </Space>
                    <Space size={8} wrap>
                      {[
                        { label: '-10%', rate: 0.9 },
                        { label: '-15%', rate: 0.85 },
                        { label: '+5%', rate: 1.05 },
                        { label: '+10%', rate: 1.1 },
                        { label: '+15%', rate: 1.15 },
                      ].map(({ label, rate }) => (
                        <Button
                          key={label}
                          type="dashed"
                          onClick={() => {
                            const basePrice = getDiscountBasePrice();
                            if (!basePrice) return;
                            setCustomPrice(
                              Number((basePrice * rate).toFixed(2))
                            );
                            form.setFieldsValue({ shopPrice: 3 });
                          }}
                        >
                          {label}
                        </Button>
                      ))}
                    </Space>
                  </Space>
                )}
            </>
          )}
          <Space size={16}>
            {currentGood && currentGood.depositFee && (
              <Form.Item label={t('order.orderDetail.depositFee')}>
                {currentGood?.depositFee}
              </Form.Item>
            )}
            {currentGood && currentGood.recycleFee && (
              <Form.Item label={t('order.orderDetail.recycleFee')}>
                {currentGood?.recycleFee}
              </Form.Item>
            )}
          </Space>

          <Space size={16}>
            <Form.Item
              name="goodsNumber"
              label={t('order.orderDetail.amount')}
              rules={[
                { required: true, message: t('general.inputError.empty') },
              ]}
            >
              <InputNumber
                type="number"
                min={1}
                autoFocus
                onPressEnter={onConfirm}
              />
            </Form.Item>
            {goodDetail && (
              <Form.Item label={t('goods.goodsListColumns.goodsNumber')}>
                <Space size={4}>
                  {matchedProduct
                    ? matchedProduct.productNumber
                    : goodDetail.goodsNumber}
                  <Button
                    type="link"
                    size="small"
                    style={{ padding: 0 }}
                    icon={<EditOutlined />}
                    onClick={() => setShowStockModal(true)}
                  />
                </Space>
              </Form.Item>
            )}
          </Space>
          {currentGood?.isWeightGoods && (
            <Form.Item
              name="goodsWeight"
              label={t('goods.goodsListColumns.goodsWeight')}
              rules={[
                {
                  required: currentGood?.isWeightGoods,
                  message: t('general.inputError.pleaseInputAmount'),
                },
                () => ({
                  validator(rule, value) {
                    if (value <= 0) {
                      return Promise.reject(
                        new Error(t('order.inputError.outOfRange'))
                      );
                    }
                    return Promise.resolve();
                  },
                }),
              ]}
            >
              <InputNumber type="number" min={0} />
            </Form.Item>
          )}
        </Form>
      </Spin>
    </Modal>
      <GoodStockModal
        visible={showStockModal}
        setVisible={setShowStockModal}
        goodInfo={goodDetail}
        refresh={getGoodDetail}
      />
    </>
  );
};

export default SelectGoodAttrModal;
